import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { unionApi } from '../lib/api'
import type {
  SaveUnionRequest,
  UnionSearchParams,
  UnionMembersParams,
  AffiliateWorksiteRequest,
  AffiliateBusinessRequest,
  UpdateBusinessAffiliationRequest,
  UpdateWorksiteAffiliationRequest,
  UpdateWorksiteOverrideRequest,
  WorksiteOverrideRequest,
  AddStewardRequest,
} from '../types/union'

// === Union Hooks ===

export function useUnionSearch(params?: UnionSearchParams) {
  return useQuery({
    queryKey: ['unions', params],
    queryFn: async () => {
      const response = await unionApi.search(params)
      return response.data
    },
  })
}

export function useUnionDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['unions', id],
    queryFn: async () => {
      if (!id) throw new Error('Union ID is required')
      const response = await unionApi.getById(id)
      return response.data
    },
    enabled: !!id,
  })
}

// Paginated + searchable member roster (DT-SCALE-1). Bounded read — never fetches the whole
// membership at once. keepPreviousData holds the current page visible while the next one loads.
export function useUnionMembers(id: string | undefined, params?: UnionMembersParams) {
  return useQuery({
    queryKey: ['unions', id, 'members', params],
    queryFn: async () => {
      if (!id) throw new Error('Union ID is required')
      const response = await unionApi.getMembers(id, params)
      return response.data
    },
    enabled: !!id,
    placeholderData: keepPreviousData,
  })
}

export function useCreateUnion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveUnionRequest) =>
      unionApi.create(data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unions'] })
    },
  })
}

export function useUpdateUnion(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveUnionRequest) =>
      unionApi.update(id, data).then(r => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(['unions', id], data)
      queryClient.invalidateQueries({ queryKey: ['unions'] })
    },
  })
}

export function useDeleteUnion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => unionApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unions'] })
    },
  })
}

// === Worksite Affiliation Hooks ===

export function useAffiliateWorksiteToUnion(unionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: AffiliateWorksiteRequest) =>
      unionApi.affiliateWorksite(unionId, data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unions', unionId] })
    },
  })
}

export function useUnaffiliateWorksiteFromUnion(unionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (worksiteId: string) =>
      unionApi.unaffiliateWorksite(unionId, worksiteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unions', unionId] })
    },
  })
}

// === Business Affiliation Hooks ===

export function useAffiliateBusinessToUnion(unionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: AffiliateBusinessRequest) =>
      unionApi.affiliateBusiness(unionId, data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unions', unionId] })
    },
  })
}

export function useUpdateBusinessAffiliation(unionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ businessId, data }: { businessId: string; data: UpdateBusinessAffiliationRequest }) =>
      unionApi.updateBusinessAffiliation(unionId, businessId, data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unions', unionId] })
    },
  })
}

export function useUnaffiliateBusinessFromUnion(unionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (businessId: string) =>
      unionApi.unaffiliateBusiness(unionId, businessId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unions', unionId] })
    },
  })
}

// === Update Worksite Affiliation Hooks ===

export function useUpdateWorksiteAffiliation(unionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ worksiteId, data }: { worksiteId: string; data: UpdateWorksiteAffiliationRequest }) =>
      unionApi.updateWorksiteAffiliation(unionId, worksiteId, data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unions', unionId] })
    },
  })
}

export function useUpdateWorksiteOverride(unionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ businessId, worksiteId, data }: { businessId: string; worksiteId: string; data: UpdateWorksiteOverrideRequest }) =>
      unionApi.updateWorksiteOverride(unionId, businessId, worksiteId, data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unions', unionId] })
    },
  })
}

// === Worksite Override Hooks ===

export function useCreateWorksiteOverride(unionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ businessId, data }: { businessId: string; data: WorksiteOverrideRequest }) =>
      unionApi.createWorksiteOverride(unionId, businessId, data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unions', unionId] })
    },
  })
}

export function useRemoveWorksiteOverride(unionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ businessId, worksiteId }: { businessId: string; worksiteId: string }) =>
      unionApi.removeWorksiteOverride(unionId, businessId, worksiteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unions', unionId] })
    },
  })
}

// === Steward Hooks ===

export function useAddSteward(unionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ worksiteId, data }: { worksiteId: string; data: AddStewardRequest }) =>
      unionApi.addSteward(unionId, worksiteId, data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unions', unionId] })
    },
  })
}

export function useRemoveSteward(unionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ worksiteId, stewardId }: { worksiteId: string; stewardId: string }) =>
      unionApi.removeSteward(unionId, worksiteId, stewardId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unions', unionId] })
    },
  })
}
